import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ComposableMap, Geographies, Geography, ZoomableGroup, useMapContext } from 'react-simple-maps';
import { useLanguage } from '@/contexts/LanguageContext';
import { useTheme } from '@/contexts/ThemeContext';
import { MapPin, Loader2, Plus, Minus, RotateCcw } from 'lucide-react';
import { IS_PRERENDER } from '@/lib/prerender';

// Sabit SVG dünya haritası (tile/API key YOK, filigran YOK)
const geoUrl = 'https://cdn.jsdelivr.net/npm/world-atlas@2/countries-110m.json';

// Tüm sabitler bileşen dışında: react-simple-maps projectionConfig/center/zoom/onMove* değişince
// projeksiyonu ya da d3-zoom davranışını yeniden kurar → yakınlaştırma sıfırlanır/takılır.
const PROJECTION_CONFIG = { scale: 235, center: [65, 28] };
const MAP_STYLE = { width: '100%', height: '100%' };
const DEFAULT_VIEW = { coordinates: [65, 28], zoom: 1 };
const GEO_STYLE = { default: { outline: 'none' }, hover: { outline: 'none' }, pressed: { outline: 'none' } };
const MIN_ZOOM = 1;
const MAX_ZOOM = 8;
const CLUSTER_PX = 26; // ekranda bu kadar pikselden yakın konumlar tek işarette toplanır
const clampZoom = (z) => Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, z));
// İşaret ekran boyutu zoom ile hafifçe büyür (k^0.25: 4x'te ~1.4, 8x'te ~1.7 kat).
// Tam ters ölçek (1/k) yakınlaşınca fazla küçük, hiç ölçeklememek fazla büyük kalıyordu.
const markerGrowth = (k) => Math.pow(k, 0.25);

// Node ikonu (public/node-icon.svg ile aynı çizim) — bir kez tanımlanır, işaretlerde <use> ile kullanılır
const NodeIconDefs = () => (
  <defs>
    <linearGradient id="tl-node-grad" x1="4" y1="2" x2="20" y2="22" gradientUnits="userSpaceOnUse">
      <stop offset="0" stopColor="#6ee7b7" />
      <stop offset="1" stopColor="#059669" />
    </linearGradient>
    <symbol id="tl-node-icon" viewBox="0 0 24 24">
      <path d="M12 1.6 20.4 4.8v6.1c0 5.6-3.5 9.7-8.4 11.5C7.1 20.6 3.6 16.5 3.6 10.9V4.8Z" fill="url(#tl-node-grad)" stroke="#047857" strokeWidth="0.8" strokeLinejoin="round" />
      <path d="m8.2 12.1 2.6 2.6 5-5.2" fill="none" stroke="#ffffff" strokeWidth="2.3" strokeLinecap="round" strokeLinejoin="round" />
    </symbol>
  </defs>
);

const TXT = {
  tr: { title: 'Node Lokasyonları', description: 'Dünya genelindeki Threat.live node\'ları', loading: 'Harita yükleniyor...', error: 'Node verileri yüklenemedi', nodeCount: 'Node Sayısı', nodes: 'node', countries: 'ÜLKE', zoomIn: 'Yakınlaştır', zoomOut: 'Uzaklaştır', reset: 'Tümünü göster', wheelHint: 'Yakınlaştırmak için Ctrl (Mac: ⌘) + kaydır', clickToZoom: 'Yakınlaştırmak için tıkla', more: 'konum daha' },
  en: { title: 'Node Locations', description: 'Threat.live nodes around the world', loading: 'Loading map...', error: 'Failed to load node data', nodeCount: 'Node Count', nodes: 'nodes', countries: 'COUNTRIES', zoomIn: 'Zoom in', zoomOut: 'Zoom out', reset: 'Show all', wheelHint: 'Use Ctrl (Mac: ⌘) + scroll to zoom', clickToZoom: 'Click to zoom in', more: 'more locations' },
};

// Aynı koordinattaki node'ları tek konumda topla (veri merkezi dağılımıyla)
const groupNodesByLocation = (rawNodes) => {
  const groups = {};
  rawNodes.forEach((node) => {
    const lat = parseFloat(node.lat);
    const lon = parseFloat(node.lon);
    if (Number.isNaN(lat) || Number.isNaN(lon)) return;
    const key = `${lat.toFixed(5)}_${lon.toFixed(5)}`;
    if (!groups[key]) groups[key] = { ...node, lat, lon, nodeCount: 0, dcCounts: {} };
    const g = groups[key];
    g.nodeCount += 1;
    g.dcCounts[node.datacenter] = (g.dcCounts[node.datacenter] || 0) + 1;
    if (!g.sponsorImg && node.sponsorImg && node.sponsorImg.trim() !== '') g.sponsorImg = node.sponsorImg;
  });
  return Object.values(groups);
};

// Çerçeve bileşen DIŞINDA: içeride tanımlıyken her render'da yeni bileşen türü olup
// tüm haritayı söküp yeniden kuruyordu (hover'da yanıp sönme + yakınlaştırmanın bozulması).
const MapShell = ({ txt, nodeTotal, countryTotal, children }) => (
  <div className="border border-border bg-card" data-testid="node-map">
    <div className="flex items-center justify-between px-5 py-3 border-b border-border bg-muted/30">
      <div className="flex items-center gap-2">
        <MapPin className="h-3.5 w-3.5 text-primary" strokeWidth={2.25} />
        <span className="text-[11px] font-mono uppercase tracking-[0.18em] text-foreground">{txt.title}</span>
        <span className="text-[10px] font-mono uppercase tracking-[0.18em] text-muted-foreground hidden md:inline">// {txt.description}</span>
      </div>
      <div className="flex items-center gap-3 text-[10px] font-mono uppercase tracking-[0.18em] text-muted-foreground">
        <span className="flex items-center gap-1.5"><span className="status-dot" />LIVE</span>
        <span className="text-border">|</span>
        <span><span className="text-foreground tabular">{nodeTotal}</span> {txt.nodes}</span>
        {countryTotal > 0 && (<><span className="text-border">|</span><span><span className="text-foreground tabular">{countryTotal}</span> {txt.countries}</span></>)}
      </div>
    </div>
    {children}
  </div>
);

// Ülke katmanı memo'lu: hover/zoom state'i değişince ~177 ülke yolu yeniden çizilmez.
const Land = React.memo(({ fill, stroke }) => (
  <Geographies geography={geoUrl}>
    {({ geographies }) => geographies.map((geo) => (
      <Geography key={geo.rsmKey} geography={geo} fill={fill} stroke={stroke} strokeWidth={0.4} style={GEO_STYLE} />
    ))}
  </Geographies>
));

// İlk yüklemede tüm node'ları kapsayacak görünümü hesaplar (bir kez).
const FitToNodes = ({ locations, onFit }) => {
  const { projection, width, height } = useMapContext();
  const done = useRef(false);
  useEffect(() => {
    if (done.current || locations.length === 0) return;
    done.current = true;
    const pts = locations.map((l) => projection([l.lon, l.lat])).filter(Boolean);
    const xs = pts.map((p) => p[0]);
    const ys = pts.map((p) => p[1]);
    const pad = 60;
    const bw = Math.max(Math.max(...xs) - Math.min(...xs), 1) + pad * 2;
    const bh = Math.max(Math.max(...ys) - Math.min(...ys), 1) + pad * 2;
    const zoom = clampZoom(Math.min(width / bw, height / bh, 3));
    const center = projection.invert([(Math.max(...xs) + Math.min(...xs)) / 2, (Math.max(...ys) + Math.min(...ys)) / 2]);
    onFit({ coordinates: center, zoom });
  }, [locations, projection, width, height, onFit]);
  return null;
};

// Ekranda birbirine yakın konumları zoom seviyesine göre kümeler; işaretler ekranda sabit boyutta kalır.
const ClusteredMarkers = React.memo(({ locations, k, onHover, onLeave, onClusterClick }) => {
  const { projection } = useMapContext();
  const clusters = useMemo(() => {
    const threshold = (CLUSTER_PX * markerGrowth(k)) / k; // harita birimi (grup k ile ölçekli)
    const pts = locations
      .map((l) => ({ l, p: projection([l.lon, l.lat]) }))
      .filter((x) => x.p)
      .sort((a, b) => b.l.nodeCount - a.l.nodeCount);
    const out = [];
    pts.forEach(({ l, p }) => {
      const c = out.find((c) => Math.hypot(c.x - p[0], c.y - p[1]) < threshold);
      if (c) {
        c.members.push(l);
        c.nodeCount += l.nodeCount;
        c.x = (c.x * (c.members.length - 1) + p[0]) / c.members.length;
        c.y = (c.y * (c.members.length - 1) + p[1]) / c.members.length;
      } else {
        out.push({ id: `${l.lat}_${l.lon}`, x: p[0], y: p[1], members: [l], nodeCount: l.nodeCount });
      }
    });
    return out;
  }, [locations, k, projection]);

  const s = markerGrowth(k) / k;
  return clusters.map((c) => {
    const multi = c.members.length > 1;
    const badgeW = c.nodeCount >= 10 ? 18 : 14;
    return (
      <g key={c.id} transform={`translate(${c.x},${c.y})`}
        onMouseEnter={(e) => onHover(c, e)} onMouseMove={(e) => onHover(c, e)} onMouseLeave={onLeave}
        onClick={multi ? () => onClusterClick(c, projection) : undefined}
        style={{ cursor: multi ? 'zoom-in' : 'pointer' }}>
        <g transform={`scale(${s})`}>
          {/* Sabit algılama alanı; animasyonlu hale fareyi algılamaz (girip-çıkma → yanıp sönme döngüsü) */}
          <circle r={13} fill="transparent" />
          <circle r={11} fill="rgba(16,185,129,0.20)" pointerEvents="none">
            <animate attributeName="r" values="9;15;9" dur="2.4s" repeatCount="indefinite" />
            <animate attributeName="opacity" values="0.45;0.05;0.45" dur="2.4s" repeatCount="indefinite" />
          </circle>
          <use href="#tl-node-icon" x={-10} y={-11} width={20} height={20} pointerEvents="none"
            style={{ filter: 'drop-shadow(0 1px 3px rgba(5,150,105,0.45))' }} />
          {c.nodeCount > 1 && (
            <g transform="translate(6,-15)" pointerEvents="none">
              <rect x={0} y={0} width={badgeW} height={12} rx={2} fill="#0f172a" stroke="rgba(16,185,129,0.6)" strokeWidth={0.75} />
              <text x={badgeW / 2} y={9} textAnchor="middle" fontSize={9} fontWeight="700" fill="#10b981" style={{ fontFamily: 'monospace' }}>×{c.nodeCount}</text>
            </g>
          )}
        </g>
      </g>
    );
  });
});

const NodeMap = () => {
  const { language } = useLanguage();
  const { theme } = useTheme();
  const txt = TXT[language] || TXT.en;
  const [nodes, setNodes] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [hovered, setHovered] = useState(null); // { cluster, x, y }
  const [view, setView] = useState(DEFAULT_VIEW); // kontrollü görünüm (butonlar / tüm node'lara sığdır)
  const [k, setK] = useState(1);                  // canlı zoom (hareket sırasında) → işaret boyutu + kümeleme
  const [showHint, setShowHint] = useState(false);
  const homeView = useRef(DEFAULT_VIEW);
  const hintTimer = useRef(null);
  const wrapRef = useRef(null);

  useEffect(() => {
    const fetchNodes = async () => {
      if (IS_PRERENDER) return;
      setIsLoading(true);
      try {
        const response = await fetch('https://api.threat.live/get_nodes');
        if (!response.ok) throw new Error('Failed to fetch nodes');
        setNodes(await response.json());
      } catch (err) {
        setError(err.message);
      } finally {
        setIsLoading(false);
      }
    };
    fetchNodes();
  }, []);

  useEffect(() => () => clearTimeout(hintTimer.current), []);

  // Fare tekerleği + Ctrl: d3 Ctrl'de hızı 10x yapar → tek tık ~4x zoom. Büyük adımları 1/4'e indirip
  // aynı noktaya yeniden gönder (imleç altı sabit kalır). Trackpad pinch'in küçük adımlarına dokunulmaz.
  useEffect(() => {
    const el = wrapRef.current;
    if (!el) return undefined;
    const scaled = new WeakSet();
    const onWheel = (e) => {
      if (scaled.has(e) || !(e.ctrlKey || e.metaKey) || Math.abs(e.deltaY) < 40) return;
      e.preventDefault();
      e.stopPropagation();
      const soft = new WheelEvent('wheel', {
        deltaY: e.deltaY / 4, deltaX: e.deltaX, deltaMode: e.deltaMode, clientX: e.clientX, clientY: e.clientY,
        screenX: e.screenX, screenY: e.screenY, ctrlKey: e.ctrlKey, metaKey: e.metaKey, bubbles: true, cancelable: true,
      });
      scaled.add(soft);
      e.target.dispatchEvent(soft);
    };
    el.addEventListener('wheel', onWheel, { capture: true, passive: false });
    return () => el.removeEventListener('wheel', onWheel, { capture: true });
  }, [isLoading, error]);

  const locations = useMemo(() => groupNodesByLocation(nodes), [nodes]);
  const countryTotal = useMemo(() => new Set(nodes.map((n) => n.country).filter(Boolean)).size, [nodes]);

  const goTo = useCallback((v) => {
    const next = { coordinates: v.coordinates, zoom: clampZoom(v.zoom) };
    setView(next);
    setK(next.zoom);
  }, []);

  const onFit = useCallback((v) => { homeView.current = v; goTo(v); }, [goTo]);

  // ZoomableGroup'a giden fonksiyonlar SABİT olmalı (değişince d3-zoom baştan kurulur)
  const handleMove = useCallback(({ zoom }) => setK((prev) => (Math.abs(prev - zoom) > 0.01 ? zoom : prev)), []);
  const handleMoveEnd = useCallback(({ coordinates, zoom }) => { setView({ coordinates, zoom }); setK(zoom); }, []);
  const filterZoomEvent = useCallback((event) => {
    if (event.type === 'wheel') {
      // Sayfa kaydırması haritaya takılmasın: tekerlekle zoom yalnız Ctrl/⌘ ile (trackpad pinch = ctrlKey)
      if (event.ctrlKey || event.metaKey) return true;
      if (!hintTimer.current) setShowHint(true);
      clearTimeout(hintTimer.current);
      hintTimer.current = setTimeout(() => { hintTimer.current = null; setShowHint(false); }, 1500);
      return false;
    }
    if (event.type === 'touchstart') return event.touches && event.touches.length > 1; // tek parmak = sayfa kaydırma
    return !event.button;
  }, []);

  const handleHover = useCallback((cluster, e) => {
    const r = wrapRef.current?.getBoundingClientRect();
    setHovered({ cluster, x: e.clientX - (r?.left || 0), y: e.clientY - (r?.top || 0) });
  }, []);
  const handleLeave = useCallback(() => setHovered(null), []);
  const handleClusterClick = useCallback((c, projection) => {
    setHovered(null);
    goTo({ coordinates: projection.invert([c.x, c.y]), zoom: k * 2.5 });
  }, [k, goTo]);

  const isDark = theme === 'dark';
  const bg = isDark ? '#0a0a0b' : '#f8fafc';
  const landFill = isDark ? '#1f2937' : '#e5e7eb';
  const landStroke = isDark ? '#374151' : '#cbd5e1';
  const btn = 'h-8 w-8 flex items-center justify-center border border-border bg-card/90 text-muted-foreground hover:text-foreground hover:bg-muted backdrop-blur-sm';

  if (isLoading || error) {
    return (
      <MapShell txt={txt} nodeTotal={nodes.length} countryTotal={countryTotal}>
        <div className="h-[500px] bg-muted/20 flex items-center justify-center">
          {isLoading
            ? <div className="flex items-center gap-2 text-muted-foreground font-mono text-xs uppercase tracking-wider"><Loader2 className="h-4 w-4 animate-spin" />{txt.loading}</div>
            : <p className="text-destructive font-mono text-xs uppercase tracking-wider">{txt.error}</p>}
        </div>
      </MapShell>
    );
  }

  const hc = hovered?.cluster;
  return (
    <MapShell txt={txt} nodeTotal={nodes.length} countryTotal={countryTotal}>
      <div ref={wrapRef} className="relative h-[500px] overflow-hidden select-none" style={{ background: bg }}>
        <ComposableMap projection="geoMercator" projectionConfig={PROJECTION_CONFIG} style={MAP_STYLE}>
          <NodeIconDefs />
          <FitToNodes locations={locations} onFit={onFit} />
          <ZoomableGroup center={view.coordinates} zoom={view.zoom} minZoom={MIN_ZOOM} maxZoom={MAX_ZOOM}
            filterZoomEvent={filterZoomEvent} onMove={handleMove} onMoveEnd={handleMoveEnd}>
            <Land fill={landFill} stroke={landStroke} />
            <ClusteredMarkers locations={locations} k={k} onHover={handleHover} onLeave={handleLeave} onClusterClick={handleClusterClick} />
          </ZoomableGroup>
        </ComposableMap>

        <div className="absolute right-3 top-3 z-10 flex flex-col gap-1">
          <button type="button" className={btn} title={txt.zoomIn} aria-label={txt.zoomIn} onClick={() => goTo({ coordinates: view.coordinates, zoom: view.zoom * 1.6 })}><Plus className="h-4 w-4" /></button>
          <button type="button" className={btn} title={txt.zoomOut} aria-label={txt.zoomOut} onClick={() => goTo({ coordinates: view.coordinates, zoom: view.zoom / 1.6 })}><Minus className="h-4 w-4" /></button>
          <button type="button" className={btn} title={txt.reset} aria-label={txt.reset} onClick={() => goTo(homeView.current)}><RotateCcw className="h-3.5 w-3.5" /></button>
        </div>

        <div className={`pointer-events-none absolute inset-x-0 bottom-4 z-10 flex justify-center transition-opacity duration-300 ${showHint ? 'opacity-100' : 'opacity-0'}`}>
          <span className="rounded-md bg-zinc-900/85 px-3 py-1.5 text-xs text-zinc-100">{txt.wheelHint}</span>
        </div>

        {hc && (
          <div className="pointer-events-none absolute z-20 rounded-md border border-zinc-200 bg-white/95 px-3 py-2 text-xs text-zinc-800 shadow-lg ring-1 ring-black/5"
            style={{ left: Math.min(hovered.x + 12, (wrapRef.current?.clientWidth || 500) - 210), top: Math.max(hovered.y - 10, 8), maxWidth: 220 }}>
            {hc.members.length === 1 ? (
              <>
                <div className="font-semibold">{hc.members[0].city}{hc.members[0].city && hc.members[0].country ? ', ' : ''}{hc.members[0].country}</div>
                <div className="text-emerald-600 font-medium mt-0.5">{txt.nodeCount}: {hc.nodeCount}</div>
                {Object.entries(hc.members[0].dcCounts).map(([dc, n]) => (
                  <div key={dc} className="text-zinc-500">{dc}{hc.members[0].nodeCount > 1 ? `: ${n} ${txt.nodes}` : ''}</div>
                ))}
                {hc.members[0].sponsorImg && (<img src={hc.members[0].sponsorImg} alt="" className="mt-1.5 max-h-8 max-w-[110px]" onError={(e) => { e.target.style.display = 'none'; }} />)}
              </>
            ) : (
              <>
                <div className="text-emerald-600 font-medium">{txt.nodeCount}: {hc.nodeCount}</div>
                {hc.members.slice(0, 6).map((m) => (
                  <div key={`${m.lat}_${m.lon}`} className="text-zinc-700">{m.city || m.country}{m.nodeCount > 1 ? ` ×${m.nodeCount}` : ''}</div>
                ))}
                {hc.members.length > 6 && <div className="text-zinc-400">+{hc.members.length - 6} {txt.more}</div>}
                <div className="mt-1 text-zinc-400">{txt.clickToZoom}</div>
              </>
            )}
          </div>
        )}
      </div>
    </MapShell>
  );
};

export default NodeMap;
